import { useState } from 'react'
import type { RunAgent } from '../slices/runDetailsSlice'
import './RunMetadata.css'

interface RunMetadataProps {
  agentName: string
  agents?: RunAgent[]
  attemptsCount?: number
  refined?: boolean
  failureCount: number
  failureIds: string[]
  tags: string[]
  branch: string
  commitHash: string
  startedAt: string | null
  finishedAt: string | null
}

function getTagBadgeClass(tag: string): string {
  const lower = tag.toLowerCase()
  if (lower.includes('security')) return 'security'
  if (lower.includes('performance')) return 'performance'
  return 'default'
}

function formatTimestamp(ts: string | null): string {
  if (!ts) return '—'
  const d = new Date(ts)
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }) + ', ' + d.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZoneName: 'short',
  })
}

function BranchIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="4" cy="4" r="2" />
      <circle cx="4" cy="12" r="2" />
      <circle cx="12" cy="6" r="2" />
      <path d="M4 6v4M10 6c-3 0-6 0-6 4" />
    </svg>
  )
}

function ClockIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="8" cy="8" r="6" />
      <path d="M8 4v4l2.5 1.5" />
    </svg>
  )
}

function CopyIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="5" y="5" width="9" height="9" rx="1" />
      <path d="M3 11H2.5A1.5 1.5 0 011 9.5v-7A1.5 1.5 0 012.5 1h7A1.5 1.5 0 0111 2.5V3" />
    </svg>
  )
}

function CheckIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 8l4 4 6-8" />
    </svg>
  )
}

function RunMetadata({
  agentName,
  agents,
  attemptsCount,
  refined,
  failureCount,
  failureIds,
  tags,
  branch,
  commitHash,
  startedAt,
  finishedAt,
}: RunMetadataProps) {
  const [copied, setCopied] = useState(false)
  const [showFailuresDetails, setShowFailuresDetails] = useState(false)

  function handleCopyCommit() {
    navigator.clipboard.writeText(commitHash).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  const agentDisplay = agents && agents.length > 1
    ? agents.map(a => a.agent_name).join(', ')
    : agentName
  const hasMultipleAgents = agents && agents.length > 1
  const hasMultipleAttempts = attemptsCount && attemptsCount > 1

  return (
    <div data-testid="run-metadata" className="run-metadata">
      <div className="run-metadata-section">
        <div className="run-metadata-section-title">Metadata</div>
        <div className="run-metadata-label">{hasMultipleAgents ? 'Agents:' : 'Agent:'}</div>
        <div className="run-metadata-value" data-testid="run-agent-name">
          {hasMultipleAgents ? (
            <div className="run-metadata-agent-list">
              {agents.map(a => (
                <span key={a.agent_id} className="run-metadata-agent-badge">{a.agent_name}</span>
              ))}
            </div>
          ) : agentDisplay}
        </div>
        {hasMultipleAttempts && (
          <>
            <div className="run-metadata-label">Attempts:</div>
            <div className="run-metadata-value" data-testid="run-attempts-count">
              {attemptsCount} per failure per agent
            </div>
          </>
        )}
        <div className="run-metadata-label">Mode:</div>
        <div className="run-metadata-value" data-testid="run-refined-mode">
          {refined ? 'Refined' : 'Clean'}
        </div>
      </div>

      <div className="run-metadata-section">
        <div className="run-metadata-label">Failures:</div>
        <div className="run-metadata-value" data-testid="run-failure-count">
          {failureCount} specific failure{failureCount !== 1 ? 's' : ''}{' '}
          <span
            className="run-metadata-details-link"
            data-testid="failures-details-link"
            onClick={() => setShowFailuresDetails(!showFailuresDetails)}
          >
            (details)
          </span>
        </div>
        {showFailuresDetails && (
          <div className="run-metadata-popover" data-testid="failures-details-popover">
            <div className="run-metadata-popover-content">
              {failureIds.length > 0 ? (
                <ul className="run-metadata-failure-list">
                  {failureIds.map((id) => (
                    <li key={id}>{id}</li>
                  ))}
                </ul>
              ) : (
                <span>No failures recorded.</span>
              )}
            </div>
          </div>
        )}

        <div className="run-metadata-label">Tags:</div>
        <div className="run-metadata-tags" data-testid="run-tags">
          {tags.length > 0 ? (
            tags.map((tag) => (
              <span
                key={tag}
                className={`run-metadata-tag-badge ${getTagBadgeClass(tag)}`}
                data-testid={`run-tag-badge-${tag}`}
              >
                {tag}
              </span>
            ))
          ) : (
            <span className="run-metadata-value">—</span>
          )}
        </div>
      </div>

      <div className="run-metadata-section">
        <div className="run-metadata-label">Branch:</div>
        <div className="run-metadata-branch-row" data-testid="run-branch">
          <span className="run-metadata-branch-icon"><BranchIcon /></span>
          <span>{branch || '—'}</span>
          {commitHash && (
            <>
              <span className="run-metadata-muted-text">(commit:</span>
              <span className="run-metadata-commit" data-testid="run-commit-hash">
                {commitHash.slice(0, 7)}
              </span>
              <span className="run-metadata-muted-text">)</span>
              <button
                data-testid="copy-commit-btn"
                className="run-metadata-copy-btn"
                onClick={handleCopyCommit}
                title="Copy commit hash"
              >
                {copied ? <CheckIcon /> : <CopyIcon />}
              </button>
            </>
          )}
        </div>

        <div className="run-metadata-label run-metadata-label-spaced">
          Started:
        </div>
        <div className="run-metadata-timestamp-row" data-testid="run-started-at">
          <span className="run-metadata-timestamp-icon"><ClockIcon /></span>
          <span>{formatTimestamp(startedAt)}</span>
        </div>

        <div className="run-metadata-label">Finished:</div>
        <div className="run-metadata-timestamp-row" data-testid="run-finished-at">
          <span className="run-metadata-timestamp-icon"><ClockIcon /></span>
          <span>{finishedAt ? formatTimestamp(finishedAt) : 'In Progress'}</span>
        </div>
      </div>
    </div>
  )
}

export default RunMetadata
